<template>
  <div>
    <!-- Activity table -->
    <div class="card"
         v-if="isGranted([permissions.PROCESS_ACTIVITY_VIEW, permissions.PROCESS_ACTIVITY_VIEW_OWN], process)"
         qid="activities-section"
    >
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <h5 class="float-left" qid="process-activities-table-title">
              {{ $t('processes.activity.title') }}
            </h5>
          </div>
          <div class="col-sm-6 text-right">
            <span v-if="isGranted(permissions.ACTIVITY_CREATE)" qid="activity-create-button">
              <ActivityCreateForm 
                :process="object"
                @reloadContent="loadActivities">
              </ActivityCreateForm>
            </span>
            <button class="btn btn-success pl-3 pr-3"
                    @click="showCreateSModal()"
                    qid="process-activities-create-button"
                    v-if="isGranted(permissions.PROCESS_ACTIVITY_CREATE, process)"
            >
              <font-awesome-icon icon="plus"/> {{ $t('processes.activity.create_button') }}
            </button>
          </div>
        </div>

        <table class="table table-responsive-md table-list-secondary">
          <thead>
          <tr>
            <th scope="col">
              {{ $t('activities.table.name') }}
            </th>
            <th scope="col">
              {{ $t('activities.table.owners') }}
            </th>
            <th scope="col">
              {{ $t('activities.table.tags') }}
            </th>
            <th scope="col">
              {{ $t('activities.table.organizations') }}
            </th>
            <th scope="col">
              {{ $t('activities.table.countries') }}
            </th>
            <th scope="col">
              {{ $t('system.last_updated') }}
            </th>
            <th width="100"></th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="object in tableItems">
              <td>
                <router-link
                    :to="{ name: 'activities.view', params: { slug: object.slug }}"
                    qid="s-view-page-link"
                >
                  {{ object.name }}
                </router-link>
              </td>
              <td>
                <span class="mr-1" v-for="user in getReducedUsers(object.users)">
                  <span qid="profile-photo">
                    <Avatar :object="user" size="xs"></Avatar>
                  </span>
                </span>
                <span v-if="object.users.length > 3"
                      class="badge cursor-pointer"
                      @click="openUsersModal(object)"
                >
                  <font-awesome-icon icon="users"/>
                  + {{ object.users.length - 3 }}
                </span>
              </td>
              <td>
                <span class="mr-1" v-for="tag in getReducedTags(object.tags_translated)" qid="p-tags-list">
                  <span class="badge mb-1" :title="tag.name_translated">
                    <font-awesome-icon icon="tag"/>
                    {{ tag.name_translated | truncate(35) }}
                  </span>
                </span>
                <span v-if="object.tags_translated.length > 3"
                      class="badge cursor-pointer"
                      @click="openTagsModal(object)"
                >
                  <font-awesome-icon icon="tags"/>
                  + {{ object.tags_translated.length - 3 }}
                </span>
              </td>
              <td>
                <span class="mr-1" v-for="organization in object.organizations" qid="p-organizations-list">
                  <span class="badge" :title="organization.name">
                    <font-awesome-icon icon="sitemap"/>
                    {{ organization.name | truncate(35) }}
                  </span>
                </span>
              </td>
              <td>
                <span class="mr-1" v-for="country in object.countries_with_flag" qid="p-countries-list">
                  <span class="badge">
                    <img v-bind:src="country.flag" height="13" class="align-baseline mr-1" /> <span>{{country.name}}</span>
                  </span>
                </span>
              </td>
              <td>
                <span class="text-normal">
                  {{ object.updated_at | formatDate }}
                </span>
              </td>
              <td align="right">
                <span class="btn btn-delete"
                      qid="delete-s-modal"
                      v-if="isGranted(permissions.PROCESS_ACTIVITY_DELETE, process)"
                      @click="showConfirmDeleteModal(object)"
                >
                  <font-awesome-icon icon="trash-alt"/>
                </span>
              </td>
            </tr>
            <tr v-if="!sLoading && !tableItems.length" qid="no-results-row" class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>


    <!-- Delete process modal template -->
    <b-modal ref="delete-ac" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-ac-title">
          {{ $t('processes.activity.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('processes.activity.delete.description') }}
        <span v-if="itemToDelete">{{ itemToDelete.name }}</span>
      </h5>
      <h5 class="text-center">
        {{ $t('processes.activity.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideSConfirmDeleteModal"
                  qid="delete-ac-cancel-button"
        >
          {{ $t('processes.activity.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteS(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-ac-submit-button"
        >
          {{ $t('processes.activity.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Add new activity modal -->
    <b-modal ref="add-activity" hide-footer @hide="hideCreateSModal">
      <div class="d-block text-center">
        <h4 qid="add-ac-title">
          {{ $t('processes.activity.create.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="sFormSubmit" qid="create-ac-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-ac-pg-label">
              {{ $t('processes.activity.create.activity_label') }}
            </label>
            <multiselect
                v-model="selectedS"
                :options="activityItems"
                :placeholder="$t('processes.activity.create.activity_placeholder')"
                label="name"
                @search-change="loadP"
                track-by="slug"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="create-ac-form-ac"
            ></multiselect>
          </b-form-group>
          <div v-if="selectedS && selectedS.description"
               class="text-center mb-3 mt-4"
          >
            <span class="cursor-pointer">
              <span class="mr-2"
                    @click="showSDescription = !showSDescription"
                    qid="expand-activity-description"
              >
                {{$t('processes.activity.create.activity_description_expand')}}
              </span>
              <font-awesome-icon icon="chevron-down" v-if="!showSDescription"/>
              <font-awesome-icon icon="chevron-up" v-if="showSDescription"/>
            </span>
          </div>
          <div v-if="selectedS && selectedS.description && showSDescription" class="mb-4 mt-2">
            <span class="text-formatted">
              {{ selectedS.description }}
            </span>
          </div>

          <div v-if="selectedS && selectedS.tags_translated && selectedS.tags_translated.length">
            <label class="mt-3">
                {{ $t('processes.activity.create.tags_label') }}
            </label>
            <br>
            <span class="mr-1" v-for="tag in selectedS.tags_translated" qid="p-tags-list">
              <span class="badge badge-info mb-1" :title="tag.name_translated">
                {{tag.name_translated | truncate(35)}}
              </span>
            </span>
          </div>

        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreateSModal"
                    qid="create-ac-cancel-button"
          >
            {{ $t('processes.activity.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    :disabled="buttonDisabled"
                    variant="success"
                    qid="create-ac-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('processes.activity.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <ShowMoreModal
        :items="selectedItem.tags_translated"
        :show="showTagsModal"
        @closeViewModal="closeTagsModal"
        type="tags"
    ></ShowMoreModal>

    <ShowMoreModal
        :items="selectedItem.users"
        :show="showUsersModal"
        @closeViewModal="closeUsersModal"
        type="users"
    ></ShowMoreModal>

  </div>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';
  import SystemViewModal from '@/views/Systems/SystemViewModal.vue';
  import ActivityCreateForm from '@/views/Activities/ActivityCreateForm.vue';

  export default {
    name: 'ActivitiesTable',
    data: function() {
      return {
        sForm: {
          system_role: '',
          rules_followed: '',
          data_objects: []
        },

        // Systems
        tableItems: [],
        selectedS: [],
        viewSModal: false,
        selectedSSort: '',
        selectedSDirection: '',
        activityItems: [],
        sLoading: true,

        error: '',
        loadingItems: false,
        itemToDelete: {},
        itemObject: {},
        selectedP: [],
        showSDescription: false,
        buttonDisabled: false,
        selectedItem: {},
        showTagsModal: false,
        showUsersModal: false,
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      }
    },
    components: {
      Avatar,
      SystemViewModal,
      ShowMoreModal,
      ActivityCreateForm
    },
    computed: {
      getClient() {
        return this.$store.state.client
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadP();
      }
    },
    methods: {
      openTagsModal(object) {
        this.selectedItem = object
        this.showTagsModal = true
      },
      closeTagsModal() {
        this.selectedItem = {}
        this.showTagsModal = false
      },
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      getReducedTags(tags) {
        let reducedTags = []
        if (tags.length > 3) {
          reducedTags = _.cloneDeep(tags);
          reducedTags = reducedTags.slice(0, 3);
        } else {
          reducedTags = tags;
        }
        return reducedTags;
      },
      getReducedUsers(users) {
        let reducedUsers = []
        if (users.length > 3) {
          reducedUsers = _.cloneDeep(users);
          reducedUsers = reducedUsers.slice(0, 3);
        } else {
          reducedUsers = users;
        }
        return reducedUsers;
      },
      showCreateSModal() {
        this.$refs['add-activity'].show()
      },
      hideCreateSModal() {
        this.error = ''
        this.selectedS = []
        this.$refs['add-activity'].hide()
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-ac'].show()
      },
      hideSConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-ac'].hide()
      },
      openViewSModal(object) {
        this.selectedS = object
        this.viewSModal = true
      },
      handleCloseViewSModal () {
        this.selectedS = []
        this.viewSModal = false;
      },
      confirmDeleteS(object) {
        let self = this;
        piincHttp.delete('processes/' + this.$route.params.slug + '/activities/' + object.slug).then(function() {
          self.hideSConfirmDeleteModal();
          self.loadActivities();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('processes.activity.delete.success'))
        }, function(error) {
          // An activity must keep at least one process (US 9369)
          if (error.status === 422) {
            self.hideSConfirmDeleteModal();
            self.$toastr('error', self.$t('processing_map.errors.last_process'))
          }
        });
      },
      sFormSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        if (this.selectedS && this.selectedS.slug) {
          formData.append("activity", this.selectedS.slug);
        }

        formData.append("process", this.$route.params.slug);

        piincHttp.post('processes/' + this.$route.params.slug + '/activities', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          self.$toastr('success', self.$t('processes.activity.create.success'))
          self.hideCreateSModal();
          self.loadActivities();
          self.$emit('updated');
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors.activity) {
              self.error += ' ' + error.data.errors.activity[0]
            }
          }
        })
      },
      loadActivities() {
        let self = this;
        piincHttp.get('processes/' + this.$route.params.slug + '/activities', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.tableItems = response.data;
          self.sLoading = false;
        }, function() {});
      },
      loadP(query) {
        let self = this;
        piincHttp.get('activities', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.activityItems = response.data.items
          if (self.activityItems && !self.activityItems.length && !query) {
            self.activityItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      }
    },
    created () {
      this.process = this.object;
      if (this.getClient && this.getClient.slug) {
        this.loadP();
      }
      // Table items
      this.loadActivities();
    }
  }
</script>
